/**
 * Unit store — the per-site SmartLink unit of the site entity and its deeply
 * nested operational state.
 *
 * One entry per loaded site, keyed by site id, holding the whole unit object
 * as the contract delivers it (relays → loads → schedules/contracts, camera
 * interface controllers → cameras → schedules, intrusion, sensors, radio,
 * firmware/current_upgrade). The `unit` socket event full-replaces the entry;
 * the lightweight `upgrade_progress` event partial-merges only the firmware
 * progress fields into current_upgrade. A site with no provisioned unit has
 * no entry.
 *
 * Contains:
 * - siteUnit slice (replaceUnit, removeUnit, mergeUpgradeProgress) + reducer
 */
import { createSlice } from '@reduxjs/toolkit';

const slice = createSlice({
  name: 'siteUnit',
  // `generatedAtBySiteId` holds each unit's last applied snapshot build time
  // (envelope metadata) for the monotonic socket guard — kept off the unit object
  // so the contract data stays clean.
  initialState: {
    bySiteId: {},
    generatedAtBySiteId: {},
    upgradeStartPendingBySiteId: {},
    latestAppliedSnapshotRequestVersionBySiteId: {},
    latestSocketSnapshotRequestVersionBySiteId: {},
    latestSnapshotRequestVersionBySiteId: {},
  },
  reducers: {
    /** Record retrieve start order before its asynchronous response can arrive. */
    markUnitSnapshotRequestStarted(state, action) {
      const { siteId, requestVersion } = action.payload;
      state.latestSnapshotRequestVersionBySiteId[String(siteId)] = requestVersion;
    },
    /** Full-replace a site's unit (retrieve or `unit` socket event). */
    replaceUnit(state, action) {
      const {
        siteId,
        unit,
        generatedAt = null,
        source = 'rest',
        requestVersion = null,
        snapshotRequestVersion = null,
      } = action.payload;
      const key = String(siteId);
      const existingGeneratedAt = state.generatedAtBySiteId[key];
      const latestAppliedRequestVersion = state.latestAppliedSnapshotRequestVersionBySiteId[key];
      const latestSocketRequestVersion = state.latestSocketSnapshotRequestVersionBySiteId[key];

      if (
        source === 'rest' &&
        ((latestSocketRequestVersion != null &&
          (requestVersion == null || requestVersion <= latestSocketRequestVersion)) ||
          (requestVersion != null &&
            latestAppliedRequestVersion != null &&
            requestVersion < latestAppliedRequestVersion))
      ) {
        return;
      }

      const upgradeStartGuard = state.upgradeStartPendingBySiteId[key];
      const isStalePreUpgradeRestSnapshot =
        source === 'rest' &&
        upgradeStartGuard != null &&
        unit?.current_upgrade == null &&
        (requestVersion == null || requestVersion <= upgradeStartGuard.snapshotRequestVersion);

      // Do not let a pre-OTA retrieve overwrite either the local start lease or
      // an active upgrade that has already arrived over the unit socket.
      if (isStalePreUpgradeRestSnapshot) return;

      state.bySiteId[key] = unit;
      state.generatedAtBySiteId[key] =
        source === 'rest' && generatedAt == null ? (existingGeneratedAt ?? null) : generatedAt;
      if (source === 'rest' && requestVersion != null) {
        state.latestAppliedSnapshotRequestVersionBySiteId[key] = requestVersion;
        if (latestSocketRequestVersion != null && requestVersion > latestSocketRequestVersion) {
          delete state.latestSocketSnapshotRequestVersionBySiteId[key];
        }
      } else if (source === 'socket' && snapshotRequestVersion != null) {
        state.latestSocketSnapshotRequestVersionBySiteId[key] = snapshotRequestVersion;
      }

      // An active unit snapshot hands the visible local lease off immediately,
      // while retaining its request-order guard until a post-lease REST
      // snapshot arrives. This prevents a late pre-lease response from
      // overwriting the active socket state after handoff.
      if (unit?.current_upgrade != null && upgradeStartGuard != null) {
        upgradeStartGuard.awaitingSnapshot = false;
      }

      if (
        source === 'rest' &&
        upgradeStartGuard != null &&
        requestVersion != null &&
        requestVersion > upgradeStartGuard.snapshotRequestVersion
      ) {
        delete state.upgradeStartPendingBySiteId[key];
      }
    },
    /** Drop a site's unit when the retrieve reports none. */
    removeUnit(state, action) {
      const { siteId, source = 'rest', requestVersion = null } = action.payload;
      const key = String(siteId);
      const latestAppliedRequestVersion = state.latestAppliedSnapshotRequestVersionBySiteId[key];
      const latestSocketRequestVersion = state.latestSocketSnapshotRequestVersionBySiteId[key];

      if (
        source === 'rest' &&
        ((latestSocketRequestVersion != null &&
          (requestVersion == null || requestVersion <= latestSocketRequestVersion)) ||
          (requestVersion != null &&
            latestAppliedRequestVersion != null &&
            requestVersion < latestAppliedRequestVersion))
      ) {
        return;
      }

      const upgradeStartGuard = state.upgradeStartPendingBySiteId[key];

      if (
        source === 'rest' &&
        upgradeStartGuard != null &&
        (requestVersion == null || requestVersion <= upgradeStartGuard.snapshotRequestVersion)
      ) {
        return;
      }

      delete state.bySiteId[key];
      // A REST snapshot has no comparable generated-at token. Retain the last
      // socket watermark so a delayed older unit event cannot resurrect state
      // (including an already-finished upgrade) after this authoritative null.
      if (source !== 'rest') {
        delete state.generatedAtBySiteId[key];
      }
      if (source === 'rest' && requestVersion != null) {
        state.latestAppliedSnapshotRequestVersionBySiteId[key] = requestVersion;
        if (latestSocketRequestVersion != null && requestVersion > latestSocketRequestVersion) {
          delete state.latestSocketSnapshotRequestVersionBySiteId[key];
        }
      }
      if (
        source === 'rest' &&
        upgradeStartGuard != null &&
        requestVersion != null &&
        requestVersion > upgradeStartGuard.snapshotRequestVersion
      ) {
        delete state.upgradeStartPendingBySiteId[key];
      }
    },
    /** Keep command launchers locked between OTA acceptance and its unit snapshot. */
    markUpgradeStarting(state, action) {
      const { siteId, batchId = null } = action.payload;
      const key = String(siteId);
      state.upgradeStartPendingBySiteId[key] = {
        awaitingSnapshot: true,
        batchId,
        snapshotRequestVersion: state.latestSnapshotRequestVersionBySiteId[key] ?? 0,
      };
    },
    /**
     * Partial-merge firmware progress into the unit's current_upgrade from the
     * lightweight `upgrade_progress` socket event. Only programming_segment,
     * end_segment, and batch_id change; every other unit field is preserved.
     * No-op when the unit or its current_upgrade is absent.
     */
    mergeUpgradeProgress(state, action) {
      const { siteId, programming_segment, end_segment, batch_id } = action.payload;
      const unit = state.bySiteId[String(siteId)];
      if (!unit?.current_upgrade) return;
      unit.current_upgrade.programming_segment = programming_segment;
      unit.current_upgrade.end_segment = end_segment;
      unit.current_upgrade.batch_id = batch_id;
    },
  },
});

export const { replaceUnit, removeUnit, markUnitSnapshotRequestStarted, markUpgradeStarting, mergeUpgradeProgress } =
  slice.actions;
export default slice.reducer;
